import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import Loader from 'UI/Application/Components/Loaders/Loader';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { getJobPositionsList } from 'JobPosition/Domain/Services/GetJobPositionsListService';
import { patchJobPosition } from 'JobPosition/Domain/Services/PatchJobPositionService';
import { getPermissionGroupsList } from 'PermissionGroup/Domain/Services/GetPermissionGroupsListService';

function TableJobPositionView() {
  const limit = 15;
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [jobPositions, setJobPositions] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [loadData, setLoadData] = useState(true);
  const [sendingApiRequest, setSendingApiRequest] = useState(false);

  // Filters
  const [filterName, setFilterName] = useState('');
  const [filterAssigned, setFilterAssigned] = useState('ALL');

  // Edit Modal State
  const [openModal, setOpenModal] = useState(false);
  const [editingJobPosition, setEditingJobPosition] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState('');

  // Load permission groups once
  useEffect(() => {
    getPermissionGroupsList({ limit: 500 }).then((response) => {
      if (response && response.records) {
        setPermissionGroups(response.records);
      }
    }).catch((err) => {
      console.error('Error fetching permission groups:', err);
    });
  }, []);

  // Fetch job positions list
  useEffect(() => {
    if (loadData) {
      setSendingApiRequest(true);
      const params = {
        offset: offset,
        limit: limit,
      };
      if (filterName && filterName.trim()) {
        params.filterName = filterName.trim();
      }
      if (filterAssigned && filterAssigned !== 'ALL') {
        params.filterAssigned = filterAssigned;
      }

      getJobPositionsList(params).then((response) => {
        if (response && response.records) {
          setJobPositions(response.records);
          setTotalRecords(response.total);
          setLastPage(Math.max(1, Math.ceil(response.total / limit)));
        }
        setLoadData(false);
      }).finally(() => {
        setSendingApiRequest(false);
      });
    }
  }, [loadData, offset, limit, filterName, filterAssigned]);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    setOffset((newPage - 1) * limit);
    setLoadData(true);
  };

  const handleFilterNameChange = (event) => {
    setFilterName(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadData(true);
  };

  const handleFilterAssignedChange = (event) => {
    setFilterAssigned(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadData(true);
  };

  const handleOpenEdit = (jp) => {
    setEditingJobPosition(jp);
    setSelectedGroupId(jp.permissionGroup?.id || '');
    setOpenModal(true);
  };

  const handleCloseEdit = () => {
    setOpenModal(false);
    setEditingJobPosition(null);
    setSelectedGroupId('');
  };

  const handleSavePermissionGroup = async () => {
    if (!editingJobPosition) return;
    try {
      setSendingApiRequest(true);
      const payload = {
        permissionGroupId: selectedGroupId ? parseInt(selectedGroupId, 10) : 0,
        clearPermissionGroup: !selectedGroupId
      };
      const updated = await patchJobPosition(editingJobPosition.id, payload);
      if (updated) {
        toast.success(`Grupo de permisos asignado correctamente al puesto "${editingJobPosition.name}"`);
        handleCloseEdit();
        setLoadData(true);
      }
    } catch (error) {
      toast.error('Error al actualizar el grupo de permisos');
    } finally {
      setSendingApiRequest(false);
    }
  };

  // Quick inline update handler
  const handleQuickAssign = async (jp, newGroupId) => {
    try {
      setSendingApiRequest(true);
      const payload = {
        permissionGroupId: newGroupId ? parseInt(newGroupId, 10) : 0,
        clearPermissionGroup: !newGroupId
      };
      const updated = await patchJobPosition(jp.id, payload);
      if (updated) {
        toast.success(`Puesto "${jp.name}" actualizado`);
        setLoadData(true);
      }
    } catch (error) {
      toast.error('Error al actualizar el grupo de permisos');
    } finally {
      setSendingApiRequest(false);
    }
  };

  return (
    <Paper sx={{ p: 3, width: '100%', borderRadius: 2 }}>
      {/* Title & Description */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <WorkOutlineIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">
            Puestos de Trabajo y Asignación de Permisos
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Asigna un grupo de permisos por defecto a cada puesto laboral. Cuando cualquier empleado ingrese o tenga este puesto, heredará automáticamente todos los permisos correspondientes.
        </Typography>
      </Box>

      {/* Filter Bar */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por nombre de puesto"
              variant="outlined"
              value={filterName}
              onChange={handleFilterNameChange}
              placeholder="Ej. Técnico, Administrativo, PV..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado de asignación</InputLabel>
              <Select
                label="Estado de asignación"
                value={filterAssigned}
                onChange={handleFilterAssignedChange}
              >
                <MenuItem value="ALL">Todos los puestos</MenuItem>
                <MenuItem value="ASSIGNED">Con grupo asignado</MenuItem>
                <MenuItem value="UNASSIGNED">Sin grupo asignado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Mostrando {jobPositions.length} de {totalRecords} puestos
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="puestos de trabajo table">
          <TableHead sx={{ bgcolor: '#f0f4f8' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Puesto de Trabajo</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 260 }}>Grupo de Permisos Asignado</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sendingApiRequest && jobPositions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            ) : jobPositions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No se encontraron puestos que coincidan con la búsqueda.
                </TableCell>
              </TableRow>
            ) : (
              jobPositions.map((jp) => {
                const assignedGroup = jp.permissionGroup;
                return (
                  <TableRow key={jp.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WorkOutlineIcon fontSize="small" sx={{ color: '#555' }} />
                        <Typography variant="body2" fontWeight="600">
                          {jp.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth sx={{ minWidth: 220 }}>
                        <Select
                          value={assignedGroup ? assignedGroup.id : ''}
                          onChange={(e) => handleQuickAssign(jp, e.target.value)}
                          displayEmpty
                          sx={{ fontSize: '0.85rem' }}
                        >
                          <MenuItem value="">
                            <em style={{ color: '#999' }}>-- Sin grupo asignado --</em>
                          </MenuItem>
                          {permissionGroups.map((pg) => (
                            <MenuItem key={pg.id} value={pg.id}>
                              {pg.description} ({pg.sku})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar y configurar grupo">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenEdit(jp)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Pagination
          count={lastPage}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
        />
      </Box>

      {/* Dialog to edit Job Position permission group */}
      <Dialog open={openModal} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkOutlineIcon color="primary" />
          Configurar Grupo de Permisos para el Puesto
        </DialogTitle>
        <DialogContent dividers>
          {editingJobPosition && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">PUESTO SELECCIONADO</Typography>
                <Typography variant="h6" fontWeight="bold">{editingJobPosition.name}</Typography>
              </Box>

              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Todos los usuarios asignados a este puesto en la empresa recibirán automáticamente las facultades del grupo de permisos que elijas.
              </Alert>

              <FormControl fullWidth>
                <InputLabel id="select-perm-group-label">Grupo de Permisos</InputLabel>
                <Select
                  labelId="select-perm-group-label"
                  label="Grupo de Permisos"
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Sin grupo asignado (Vacío)</em>
                  </MenuItem>
                  {permissionGroups.map((pg) => (
                    <MenuItem key={pg.id} value={pg.id}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{pg.description}</Typography>
                        <Typography variant="caption" color="text.secondary">{pg.sku}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEdit} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSavePermissionGroup}
            startIcon={<CheckCircleIcon />}
          >
            Guardar Asignación
          </Button>
        </DialogActions>
      </Dialog>

      <DialogLoader open={sendingApiRequest} />
    </Paper>
  );
}

export default TableJobPositionView;
